import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { searchReview, type ReviewInDb, type ReviewSearchQuery } from '@/api';
import { useKanbanStore } from '@/stores/kanbanStore';
import { safeStringify } from '@/utils/jsonHelpers';
import { logger } from '@/utils/logger';
import {
  mergeFilterOptions,
  unionFilterOptions,
  type ReviewFilterOptions,
} from './useReviewSearchFilters';

/** How far one column has been paged, and whether there is more behind it. */
export interface ReviewColumnPagination {
  offset: number;
  limit: number;
  hasMore: boolean;
  totalCount: number;
}

export interface UseReviewBoardDataOptions {
  /** Free-text search, shared with the table half. */
  search: Ref<string>;
  /** Column filters as `searchReview` takes them, minus `kanban_state_uid`. */
  columnFilters: Ref<Record<string, string[]>>;
  /** Cards per column page. */
  pageSize: Ref<number>;
}

export interface UseReviewBoardDataReturn {
  reviewsByStateUid: Ref<Record<string, ReviewInDb[]>>;
  statePagination: Ref<Record<string, ReviewColumnPagination>>;
  stateLoading: Ref<Record<string, boolean>>;
  /** Union of every column's reported options — what the filter panel offers. */
  filterOptions: ComputedRef<ReviewFilterOptions>;
  loading: ComputedRef<boolean>;
  fetchColumn: (
    stateUid: string,
    options?: { append?: boolean; maintainLoadedCount?: boolean }
  ) => Promise<void>;
  loadMore: (stateUid: string) => Promise<void>;
  refreshAll: (options?: { maintainLoadedCount?: boolean }) => Promise<void>;
  moveCard: (
    reviewUid: string,
    targetStateUid: string,
    position: number,
    siblings?: string[]
  ) => Promise<void>;
}

/**
 * The board's data layer: one `searchReview` per rendered column, paged
 * independently, plus the single mutation path a drop goes through.
 *
 * A column is its own page of results because `searchReview` filters by one
 * `kanban_state_uid` at a time — which is also why the board can carry no
 * `page` in the URL, and why `filter_options` have to be unioned across columns
 * rather than taken from whichever response resolved last.
 */
export function useReviewBoardData(options: UseReviewBoardDataOptions): UseReviewBoardDataReturn {
  const { search, columnFilters, pageSize } = options;
  const kanbanStore = useKanbanStore();

  const reviewsByStateUid = ref<Record<string, ReviewInDb[]>>({});
  const statePagination = ref<Record<string, ReviewColumnPagination>>({});
  const stateLoading = ref<Record<string, boolean>>({});
  const filterOptionsByState = ref<Record<string, ReviewFilterOptions>>({});

  const filterOptions = computed<ReviewFilterOptions>(() =>
    unionFilterOptions(filterOptionsByState.value)
  );

  const loading = computed(() => Object.values(stateLoading.value).some(Boolean));

  const hasActiveFilters = computed(() => Object.keys(columnFilters.value).length > 0);

  function ensureColumn(stateUid: string): ReviewColumnPagination {
    // A column that has never been fetched has no entry yet, which the index
    // signature cannot express.
    if (Object.hasOwn(statePagination.value, stateUid)) {
      const existing = statePagination.value[stateUid];
      existing.limit = pageSize.value;
      return existing;
    }
    const fresh: ReviewColumnPagination = {
      offset: 0,
      limit: pageSize.value,
      hasMore: false,
      totalCount: 0,
    };
    statePagination.value[stateUid] = fresh;
    reviewsByStateUid.value[stateUid] ??= [];
    stateLoading.value[stateUid] ??= false;
    return fresh;
  }

  async function fetchColumn(
    stateUid: string,
    { append = false, maintainLoadedCount = false } = {}
  ): Promise<void> {
    const pagination = ensureColumn(stateUid);
    const loaded = reviewsByStateUid.value[stateUid] ?? [];

    // Refetching after a move must not silently collapse a column the user had
    // paged through, so re-request everything currently on screen.
    const fetchLimit =
      maintainLoadedCount && !append && loaded.length > 0 ? loaded.length : pagination.limit;

    const state = kanbanStore.kanbanStates.find(s => s.uid === stateUid);

    try {
      stateLoading.value[stateUid] = true;

      const query: ReviewSearchQuery = {
        text: search.value,
        limit: append ? pagination.limit : fetchLimit,
        offset: append ? pagination.offset : 0,
        // Drag order is stored as this; sorting by anything else would show an
        // order the next drop then contradicts.
        sort_key: 'kanban_column_sorting_position',
        sort_direction: 'asc',
        similarity_threshold: 0.5,
        column_filters: { ...columnFilters.value, kanban_state_uid: [stateUid] },
        include_archived_tickets: state?.archive_state ?? false,
      };

      const response = await searchReview({ body: query });
      if (!response.data) throw new Error('Failed to fetch reviews for state');
      const result = response.data;

      pagination.totalCount = result.result_count;

      const reported: ReviewFilterOptions = {};
      for (const [key, values] of Object.entries(result.filter_options)) {
        reported[key] = values.map(v => (typeof v === 'string' ? v : safeStringify(v ?? null)));
      }

      // With a filter applied the backend narrows filter_options to what matches
      // it, so replacing would collapse the menu to the value already picked and
      // strand you there — you could never switch from one assignee to another
      // without clearing first. Unfiltered, the response describes the whole
      // column and is safe to take wholesale.
      filterOptionsByState.value[stateUid] = hasActiveFilters.value
        ? mergeFilterOptions(filterOptionsByState.value[stateUid], reported)
        : reported;

      if (append) {
        reviewsByStateUid.value[stateUid] = [...loaded, ...result.results];
        pagination.offset += pagination.limit;
      } else {
        reviewsByStateUid.value[stateUid] = result.results;
        pagination.offset = maintainLoadedCount ? fetchLimit : pagination.limit;
      }

      pagination.hasMore = reviewsByStateUid.value[stateUid].length < pagination.totalCount;
    } catch (error) {
      logger.error(`Error fetching reviews for state ${stateUid}:`, error);
      throw error;
    } finally {
      stateLoading.value[stateUid] = false;
    }
  }

  async function loadMore(stateUid: string): Promise<void> {
    await fetchColumn(stateUid, { append: true });
  }

  async function refreshAll({ maintainLoadedCount = false } = {}): Promise<void> {
    // Archived columns are not drawn, so they are not fetched either.
    const columns = kanbanStore.boardColumns;
    await Promise.all(columns.map(state => fetchColumn(state.uid, { maintainLoadedCount })));
  }

  /**
   * The one write a drop performs. `moveReview` sets the kanban state and the
   * sort positions together — passing the target column's uids renumbers the
   * whole column, so the dropped card keeps the index it was dropped at.
   */
  async function moveCard(
    reviewUid: string,
    targetStateUid: string,
    position: number,
    siblings?: string[]
  ): Promise<void> {
    await kanbanStore.moveReview(reviewUid, targetStateUid, position, siblings);
    await refreshAll({ maintainLoadedCount: true });
  }

  return {
    reviewsByStateUid,
    statePagination,
    stateLoading,
    filterOptions,
    loading,
    fetchColumn,
    loadMore,
    refreshAll,
    moveCard,
  };
}
